// Insurance A/R — small helpers.
import type { ArClaim, ArMoney, LateReasonKind, WriteOffReason } from './types';
import { usuallyWords } from './payers';
import { getFlagSettings } from './flagSettings';

/** The mock "today" of Insurance A/R and the Dashboard (the board was drawn on Oct 15). */
export const AR_TODAY_ISO = '2026-10-15';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1, d ?? 1));
}

/** "2026-10-15" + 30 → "2026-11-14". */
export function isoPlusDays(iso: string, n: number): string {
  const d = parseIso(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Whole days from one ISO date to another (negative when `toIso` is earlier). */
export function isoDaysBetween(fromIso: string, toIso: string): number {
  return Math.round((parseIso(toIso).getTime() - parseIso(fromIso).getTime()) / 86_400_000);
}

/** "2026-11-14" → "Nov 14". */
export function isoLabel(iso: string): string {
  const d = parseIso(iso);
  return `${MONTHS[d.getUTCMonth()] ?? 'Jan'} ${String(d.getUTCDate())}`;
}

/** ELVA's checks run on business days: a Saturday or Sunday moves to Monday. */
export function nextBusinessDayIso(iso: string): string {
  const dow = parseIso(iso).getUTCDay();
  if (dow === 6) return isoPlusDays(iso, 2);
  if (dow === 0) return isoPlusDays(iso, 1);
  return iso;
}

// ---- why a claim is late (D311) ----
// A claim is late once it is past its follow-up start: the payer's usual time, or earlier the
// practice's unpaid threshold (Settings › Follow-up, 60 days by default) or a state's prompt-pay
// deadline. ONE helper, used by the A/R rows, the detail card and the Dashboard.

export interface LateReason {
  kind: LateReasonKind;
  /** The day the claim became late (its follow-up start). */
  startIso: string;
  /** The row's words: "BCBS usually pays in about 3 weeks". */
  line: string;
}

type ReasonInput = Pick<ArClaim, 'sentIso' | 'usualDays' | 'stateLimit' | 'payer' | 'days'>;

/** The reason a claim is late, or null when it hasn't reached its follow-up start yet. */
export function lateReasonOf(c: ReasonInput, flagDays: number = getFlagSettings().unpaidDays): LateReason | null {
  const options: LateReason[] = [];
  if (c.usualDays !== undefined) {
    options.push({
      kind: 'usual',
      startIso: isoPlusDays(c.sentIso, c.usualDays),
      line: `${c.payer.short} usually pays in ${usuallyWords(c.usualDays)}`,
    });
  }
  if (c.stateLimit) {
    options.push({
      kind: 'state',
      startIso: c.stateLimit.sinceIso,
      line: `Past ${c.stateLimit.state}’s ${String(c.stateLimit.days)}-day limit since ${c.stateLimit.sinceLabel}`,
    });
  }
  options.push({
    kind: 'threshold',
    startIso: isoPlusDays(c.sentIso, flagDays),
    line: `Unpaid ${String(c.days)} days; you flag claims after ${String(flagDays)}`,
  });
  let first = options[0];
  for (const o of options) if (first && o.startIso < first.startIso) first = o;
  if (!first || first.startIso >= AR_TODAY_ISO) return null;
  return first;
}

/** A late claim's reason kind; a claim with no clear reason counts as the payer's usual time. */
export function lateReasonKind(c: ReasonInput, flagDays?: number): LateReasonKind {
  return lateReasonOf(c, flagDays)?.kind ?? 'usual';
}

/**
 * "68 past the payer's usual time · 4 by your 60-day flag": the Dashboard's Late meta, built from
 * each claim's own reason (the same reasons the A/R rows show).
 */
export function lateReasonSummary(claims: ArClaim[], flagDays: number = getFlagSettings().unpaidDays): {
  counts: Record<LateReasonKind, number>;
  text: string;
} {
  const counts: Record<LateReasonKind, number> = { usual: 0, threshold: 0, state: 0 };
  for (const c of claims) counts[lateReasonKind(c, flagDays)] += 1;
  const parts: string[] = [];
  if (counts.usual > 0) parts.push(`${String(counts.usual)} past the payer’s usual time`);
  if (counts.state > 0) parts.push(`${String(counts.state)} past a state’s deadline`);
  if (counts.threshold > 0) parts.push(`${String(counts.threshold)} by your ${String(flagDays)}-day flag`);
  return { counts, text: parts.join(' · ') };
}

/** When ELVA expects the payer to have paid: sent date plus the payer's usual time. Unset = unknown. */
export function expectedPayIso(c: Pick<ArClaim, 'sentIso' | 'usualDays'>): string | undefined {
  return c.usualDays === undefined ? undefined : isoPlusDays(c.sentIso, c.usualDays);
}

/** The read-only amounts for the Stop dialog (derived from the balance unless the claim has its own). */
export function moneyFor(c: Pick<ArClaim, 'balance' | 'money'>): ArMoney {
  return c.money ?? {
    pmsPending: c.balance,
    elvaExpected: c.balance,
    patientShare: Math.round(c.balance * 25) / 100,
  };
}

/** Pause until a date: default 30 days from today, at most 90 (§5.1). */
export const PAUSE_DEFAULT_DAYS = 30;
export const PAUSE_MAX_DAYS = 90;

export const IAR_USER = { name: 'Ava Thompson', org: 'Lone Star Billing' } as const;
export const IAR_ACTOR = `${IAR_USER.name} (${IAR_USER.org})`;
export const IAR_ACTOR_DOT = `${IAR_USER.name} · ${IAR_USER.org}`;

/**
 * Who changed Follow-up settings (D165). A billing company's staff can't change
 * practice settings, so the changer is a practice owner/admin — not Ava Thompson
 * of Lone Star Billing (the staging bug).
 */
export const IAR_SETTINGS_CHANGER = 'Dr. Emily Carter (practice owner)';

/**
 * The ONE write-off reason list, used everywhere a reason is asked for — the
 * Stop dialog and "Add a reason" (staging fix §7: "One reason list everywhere").
 * Neutral wording so the same label reads correctly in both places.
 */
export const WRITE_OFF_REASONS: { id: WriteOffReason; label: string }[] = [
  { id: 'write_off', label: 'Write it off: we won’t collect it' },
  { id: 'patient_pays', label: 'The patient will pay it' },
  { id: 'other_insurance', label: 'It was billed to another insurance' },
  { id: 'something_else', label: 'Something else' },
];

/** The reason in recorded words, for the Written off "Why" column. */
export const WRITE_OFF_REASON_WORDS: Record<WriteOffReason, string> = {
  write_off: 'Written off: the practice won’t collect it',
  patient_pays: 'The patient will pay it',
  other_insurance: 'Billed to another insurance',
  something_else: 'Something else',
};

/** $38,412.60 — two decimals, thousands separators. */
export function money(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "$38,412.60" without cents when whole, e.g. baseline "about $4,860". */
export function moneyRound(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

/** Parse a user-typed amount like "1,028.00" back to a number. */
export function parseAmount(raw: string): number {
  const n = Number(raw.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}
